import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Company } from '../../../@types/Company';
import type { SentMailHistoryItem, SentMailHistoryPagination, SentMailHistoryResponse } from '../../../@types/SentMailHistory';
import type { RootState } from '../../../store/store';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import EmailPreview from '../../Prospecting/OwnerCommunication/components/EmailPreview/EmailPreview';

const DEFAULT_ITEMS_PER_PAGE = 10;
const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

interface SentMailHistoryErrorResponse {
  errors?: Array<{ message?: string }>;
  message?: string;
}

const DEFAULT_PAGINATION: SentMailHistoryPagination = {
  totalResults: 0,
  totalPages: 0,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  currentPage: 1,
  previousPage: null,
  nextPage: null,
};

function isSentMailHistoryResponse(value: unknown): value is SentMailHistoryResponse {
  if (!value || 'object' !== typeof value) {
    return false;
  }

  const payload = value as Partial<SentMailHistoryResponse>;
  const pagination = payload.pagination as Partial<SentMailHistoryPagination> | undefined;

  return (
    !!pagination &&
    'number' === typeof pagination.totalResults &&
    'number' === typeof pagination.totalPages &&
    'number' === typeof pagination.itemsPerPage &&
    'number' === typeof pagination.currentPage &&
    Array.isArray(payload.results) &&
    Array.isArray(payload.availableSenders)
  );
}

function getErrorMessage(value: unknown): string | undefined {
  if (!value || 'object' !== typeof value) {
    return undefined;
  }

  const payload = value as SentMailHistoryErrorResponse;
  if (Array.isArray(payload.errors)) {
    const firstMessage = payload.errors.find((error) => 'string' === typeof error?.message && '' !== error.message.trim())?.message;
    if (firstMessage) {
      return firstMessage;
    }
  }

  if ('string' === typeof payload.message && '' !== payload.message.trim()) {
    return payload.message;
  }

  return undefined;
}

function formatDate(value: string | null): string {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPreview(content: string | null): string {
  if (!content) {
    return '-';
  }

  const normalized = content.replaceAll(/\s+/g, ' ').trim();
  if (normalized.length <= 160) {
    return normalized;
  }

  return `${normalized.slice(0, 160)}...`;
}

export default function SentMailsHistory() {
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SentMailHistoryItem[]>([]);
  const [pagination, setPagination] = useState<SentMailHistoryPagination>(DEFAULT_PAGINATION);
  const [availableSenders, setAvailableSenders] = useState<string[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [fromFilter, setFromFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedMail, setSelectedMail] = useState<SentMailHistoryItem | null>(null);

  const hasMultipleSenders = availableSenders.length > 1;
  const senderFilterDescription = hasMultipleSenders
    ? 'Basé sur les emails précédemment envoyés.'
    : 'Basé sur les emails précédemment envoyés. Le filtre sera activé dès que plusieurs expéditeurs seront détectés.';

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const loadSentMails = useCallback(async() => {
    if (!company?.id || !jwt) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('step', String(currentPage));
      params.set('limit', String(itemsPerPage));
      params.set('sortField', 'sentAt');
      params.set('sortDirection', sortDirection);

      if (debouncedSearch) {
        params.set('filterSearch', debouncedSearch);
      }

      if (fromFilter) {
        params.set('filterFromMail', fromFilter);
      }

      const response = await fetch(`/api/companies/${company.id}/mail-config/sent-mails?${params.toString()}`, {
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
      });

      const data: unknown = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage = getErrorMessage(data);
        throw new Error(errorMessage || 'Erreur lors du chargement des emails envoyés.');
      }

      if (!isSentMailHistoryResponse(data)) {
        throw new Error('Format de reponse inattendu lors du chargement des emails envoyes.');
      }

      const nextPagination: SentMailHistoryPagination = {
        ...DEFAULT_PAGINATION,
        ...data.pagination,
      };
      const safeCurrentPage = nextPagination.currentPage > 0 ? nextPagination.currentPage : 1;

      setPagination({
        ...nextPagination,
        currentPage: safeCurrentPage,
      });
      setCurrentPage(safeCurrentPage);
      setRows(data.results);

      const senderValues = data.availableSenders.filter((sender) => typeof sender === 'string' && '' !== sender.trim());

      setAvailableSenders(senderValues);
      if (fromFilter && !senderValues.includes(fromFilter)) {
        setFromFilter('');
      }
    } catch (error) {
      console.error(error);
      showToast('error', error instanceof Error ? error.message : 'Erreur lors du chargement des emails envoyés.');
    } finally {
      setLoading(false);
    }
  }, [company?.id, currentPage, debouncedSearch, fromFilter, itemsPerPage, jwt, sortDirection]);

  useEffect(() => {
    loadSentMails();
  }, [loadSentMails]);

  const sentLabel = useMemo(
    () => `${pagination.totalResults} email${pagination.totalResults > 1 ? 's' : ''} envoyé${pagination.totalResults > 1 ? 's' : ''}`,
    [pagination.totalResults],
  );

  const openMailDetails = (mail: SentMailHistoryItem) => {
    setSelectedMail(mail);
  };

  const closeMailDetails = () => {
    setSelectedMail(null);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">Filtres de recherche</h2>
          <p className="mt-1 text-sm text-gray-600">
            Recherchez un email par objet, contenu, expéditeur ou destinataire.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <label className="mb-1 block text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Recherche
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Ex: Durand, accord RGPD, contact@..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Expéditeur
            </label>
            <select
              value={fromFilter}
              onChange={(event) => {
                setFromFilter(event.target.value);
                setCurrentPage(1);
              }}
              disabled={!hasMultipleSenders}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="">Tous les expéditeurs</option>
              {availableSenders.map((sender) => (
                <option key={sender} value={sender}>{sender}</option>
              ))}
            </select>
            <p className={`mt-1 text-xs ${hasMultipleSenders ? 'text-gray-500' : 'text-gray-400'}`}>
              {senderFilterDescription}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Tri
            </label>
            <select
              value={sortDirection}
              onChange={(event) => {
                setSortDirection(event.target.value as 'asc' | 'desc');
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            >
              <option value="desc">Plus récents d'abord</option>
              <option value="asc">Plus anciens d'abord</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <p className="text-sm font-medium text-gray-700">{sentLabel}</p>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Par page</label>
            <select
              value={itemsPerPage}
              onChange={(event) => {
                setItemsPerPage(Number(event.target.value));
                setCurrentPage(1);
              }}
              className="rounded-lg border border-gray-300 px-2 py-1 pr-10 text-sm"
            >
              {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-md">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-base font-semibold text-gray-800">Aucun email envoyé trouvé.</p>
            <p className="mt-2 text-sm text-gray-600">
              Essayez de modifier les filtres ou envoyez un premier email depuis le mail référent.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <p className="mb-3 text-sm text-gray-600">
              Cliquez sur une ligne pour afficher le contenu complet de l'email.
            </p>
            <table className="min-w-full divide-y divide-gray-200 text-left">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Date d'envoi</th>
                  <th className="px-3 py-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Expéditeur</th>
                  <th className="px-3 py-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Destinataire</th>
                  <th className="px-3 py-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Objet</th>
                  <th className="px-3 py-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Aperçu du contenu</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((mail) => (
                  <tr
                    key={mail.id}
                    className="align-top transition-colors hover:cursor-pointer hover:bg-gray-50"
                    onClick={() => openMailDetails(mail)}
                    onKeyDown={(event) => {
                      if ('Enter' === event.key || ' ' === event.key) {
                        event.preventDefault();
                        openMailDetails(mail);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Afficher le detail du mail envoye a ${mail.receiverMail}`}
                  >
                    <td className="px-3 py-3 text-sm text-gray-700">{formatDate(mail.sentAt || mail.createdAt)}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      <div className="font-medium text-gray-900">{mail.fromName || '-'}</div>
                      <div className="text-xs text-gray-500">{mail.fromMail}</div>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">{mail.receiverMail}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">{mail.subject || '-'}</td>
                    <td className="max-w-md px-3 py-3 text-sm text-gray-700">{formatPreview(mail.content)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setCurrentPage((previousPage) => Math.max(1, previousPage - 1))}
            disabled={loading || null === pagination.previousPage}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Page précédente
          </button>

          <p className="text-sm text-gray-600">
            Page {pagination.currentPage} / {Math.max(pagination.totalPages, 1)}
          </p>

          <button
            type="button"
            onClick={() => setCurrentPage((previousPage) => previousPage + 1)}
            disabled={loading || null === pagination.nextPage}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Page suivante
          </button>
        </div>
      </div>
      {selectedMail && (
        <EmailPreview
          isOpen={null !== selectedMail}
          onClose={closeMailDetails}
          emailContent={selectedMail.content || ''}
          emailSubject={selectedMail.subject || '(Sans objet)'}
          emailTo={selectedMail.receiverMail}
          viewOnly
        />
      )}
    </div>
  );
}
